/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file driverPushPayload.js
 * @description
 * Pure (no React Native imports) vocabulary + parsing for the Driver / Courier
 * push contract. FCM `data` values always arrive as strings, so every field is
 * read defensively and a malformed `changeSummary` JSON degrades to "no summary"
 * instead of throwing inside a notification tap handler.
 *
 * Payload (backend contract): `{type:'driver', eventKey, routePlanId?, stopId?,
 * warehouseId?, changeRequestId?, status?, changeSummary?, deepLink?}`.
 *
 * @module helpers/driverPushPayload
 */

/** Every eventKey the backend sends for the driver app. */
export const DRIVER_EVENT_KEYS = {
    routeAssigned: 'driver.route.assigned',
    routeUpdated: 'driver.route.updated',
    routeUnassigned: 'driver.route.unassigned',
    routeCancelled: 'driver.route.cancelled',
    stockLoaded: 'driver.stock.loaded',
    changeRequestResolved: 'driver.changeRequest.resolved',
};

/** Brand orange used by every driver notification surface. */
export const DRIVER_PUSH_ORANGE = '#F97316';

const pick = (source, ...keys) => {
    if (!source || typeof source !== 'object') {
        return '';
    }
    for (const key of keys) {
        const value = source[key];

        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return String(value).trim();
        }
    }

    return '';
};

const CANONICAL_EVENT_KEYS = Object.values(DRIVER_EVENT_KEYS).reduce((acc, key) => {
    acc[key.toLowerCase()] = key;

    return acc;
}, {});

/**
 * eventKey of a payload, mapped onto the canonical camelCase spelling
 * (`driver.changeRequest.resolved`) when it is a known driver key, so callers
 * can compare against {@link DRIVER_EVENT_KEYS} whatever casing the producer used.
 * @param {Object} data Notification `data`.
 * @returns {string} The eventKey or ''.
 */
export const driverEventKeyOf = (data) => {
    const raw = pick(data, 'eventKey', 'eventkey');

    return CANONICAL_EVENT_KEYS[raw.toLowerCase()] || raw;
};

/**
 * Whether a payload belongs to the driver app (`type: 'driver'` or `driver.*`).
 * @param {Object} data Notification `data`.
 * @returns {boolean} True for driver pushes.
 */
export const isDriverPushData = (data) => {
    if (!data || typeof data !== 'object') {
        return false;
    }

    return pick(data, 'type').toLowerCase() === 'driver' || driverEventKeyOf(data).toLowerCase().startsWith('driver.');
};

const idList = (value) => (Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : []);

/**
 * Parse `changeSummary` (JSON string from FCM, or an object from an inbox row's
 * metadata) into a normalized shape. Never throws.
 *
 * @param {string|Object|null|undefined} raw Raw value.
 * @returns {?{added: string[], removed: string[], reordered: boolean, timeChanged: boolean,
 *   detailsChanged: string[], plannedDate: string, plannedStartTime: string}} Summary, or null
 *   when absent / malformed / carrying no information.
 */
export const parseChangeSummary = (raw) => {
    let value = raw;

    if (typeof value === 'string') {
        const text = value.trim();

        if (!text) {
            return null;
        }
        try {
            value = JSON.parse(text);
        } catch (_error) {
            return null;
        }
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    const summary = {
        added: idList(value.added),
        removed: idList(value.removed),
        reordered: value.reordered === true || value.reordered === 'true',
        timeChanged: value.timeChanged === true || value.timeChanged === 'true',
        detailsChanged: idList(value.detailsChanged),
        plannedDate: value.plannedDate ? String(value.plannedDate) : '',
        plannedStartTime: value.plannedStartTime ? String(value.plannedStartTime) : '',
    };
    const empty = !summary.added.length && !summary.removed.length && !summary.detailsChanged.length
        && !summary.reordered && !summary.timeChanged;

    return empty ? null : summary;
};

/**
 * Flatten a push `message` / `data` into the payload the in-app surfaces use.
 * @param {{data?: Object, notification?: {title?: string, body?: string}}|Object} message FCM message or bare data.
 * @returns {{eventKey: string, routePlanId: string, stopId: string, warehouseId: string,
 *   changeRequestId: string, status: string, changeSummary: ?Object, title: string, body: string}} Payload.
 */
export const buildDriverPushPayload = (message) => {
    const data = message?.data || message || {};

    return {
        eventKey: driverEventKeyOf(data),
        routePlanId: pick(data, 'routePlanId', 'routeplanid', 'routeId'),
        stopId: pick(data, 'stopId', 'stopid'),
        warehouseId: pick(data, 'warehouseId', 'warehouseid'),
        changeRequestId: pick(data, 'changeRequestId', 'changerequestid'),
        status: pick(data, 'status').toLowerCase(),
        changeSummary: parseChangeSummary(data.changeSummary ?? data.changesummary),
        title: String(message?.notification?.title || data.title || ''),
        body: String(message?.notification?.body || data.body || ''),
    };
};

/**
 * How a stop was affected by the change a push described.
 * @param {?Object} summary Parsed change summary (see {@link parseChangeSummary}).
 * @param {string} stopId Stop `_id`.
 * @returns {'new'|'changed'|null} Highlight kind.
 */
export const stopChangeKind = (summary, stopId) => {
    if (!summary || !stopId) {
        return null;
    }
    const id = String(stopId);

    if (summary.added.includes(id)) {
        return 'new';
    }

    return summary.detailsChanged.includes(id) ? 'changed' : null;
};
